export const SCHEDULE_TRANSFER_FORMAT = 'pi-schedule-schedule';
export const SCHEDULE_TRANSFER_VERSION = 1;

const PLACEHOLDER = '__PLACEHOLDER__';

const isBlank = (value) => value === undefined || value === null || String(value).trim() === '';

const asTrimmedString = (value) => {
    if (isBlank(value)) return null;
    const text = String(value).trim();
    if (text === 'null' || text === 'undefined') return null;
    return text;
};

const asOptionalInteger = (value) => {
    if (isBlank(value)) return null;
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? null : parsed;
};

const uniqueStrings = (values) => {
    const seen = new Set();
    const result = [];
    (Array.isArray(values) ? values : []).forEach((value) => {
        const text = asTrimmedString(value);
        if (!text || seen.has(text)) return;
        seen.add(text);
        result.push(text);
    });
    return result;
};

const nameFromAssociation = (value, fallbacks = []) => {
    if (typeof value === 'string') return asTrimmedString(value);
    if (value && typeof value === 'object') {
        const fromObject = asTrimmedString(
            value.name ?? value.teacher_name ?? value.full_name ?? value.group_code ?? value.room_code ?? value.code
        );
        if (fromObject) return fromObject;
    }
    for (const fallback of fallbacks) {
        const text = asTrimmedString(fallback);
        if (text) return text;
    }
    return null;
};

const collectTeacherNames = (entry = {}) => {
    const names = [];
    if (Array.isArray(entry.teachers)) {
        entry.teachers.forEach((teacher) => {
            const name = nameFromAssociation(teacher);
            if (name) names.push(name);
        });
    }
    if (Array.isArray(entry.teacher_names)) {
        entry.teacher_names.forEach((name) => {
            const text = asTrimmedString(name);
            if (text) names.push(text);
        });
    }
    const single = nameFromAssociation(entry.teacher, [entry.teacher_name]);
    if (nameFromAssociation(entry.host) && String(entry.entry_type || entry.type || '').toLowerCase() === 'event') {
        names.push(nameFromAssociation(entry.host));
    }
    if (single) names.push(single);
    return uniqueStrings(names);
};

const collectGroupCodes = (entry = {}) => {
    const codes = [];
    const pushGroup = (group) => {
        if (typeof group === 'string' || typeof group === 'number') {
            const text = asTrimmedString(group);
            if (text) codes.push(text);
            return;
        }
        if (group && typeof group === 'object') {
            const text = asTrimmedString(group.group_code ?? group.code ?? group.name ?? group.id);
            if (text) codes.push(text);
        }
    };

    if (Array.isArray(entry.groups)) entry.groups.forEach(pushGroup);
    if (Array.isArray(entry.group_codes)) entry.group_codes.forEach(pushGroup);
    if (Array.isArray(entry.group_ids)) entry.group_ids.forEach(pushGroup);
    if (entry.group) pushGroup(entry.group);
    return uniqueStrings(codes);
};

const collectRoom = (entry = {}) => {
    const roomObject = entry.room && typeof entry.room === 'object' ? entry.room : null;
    const roomCode = asTrimmedString(
        entry.room_code
        ?? roomObject?.room_code
        ?? roomObject?.code
        ?? (typeof entry.room === 'string' ? entry.room : null)
    );
    const roomName = asTrimmedString(entry.room_name ?? roomObject?.name ?? roomCode);
    return {
        room_code: roomCode || roomName,
        room_name: roomName || roomCode
    };
};

const normalizeTime = (value) => {
    const text = asTrimmedString(value);
    if (!text) return null;
    if (/^\d{2}:\d{2}$/.test(text)) return `${text}:00`;
    if (/^\d{2}:\d{2}:\d{2}$/.test(text)) return text;
    return text;
};

const isPlaceholderEntry = (entry) => {
    if (!entry || typeof entry !== 'object') return true;
    return [entry.subject_name, entry.teacher_name, entry.room_name, entry.room_code]
        .some((value) => asTrimmedString(value) === PLACEHOLDER);
};

export function serializeScheduleTransferEntry(entry = {}) {
    if (!entry || typeof entry !== 'object') return null;

    const entryType = asTrimmedString(entry.entry_type ?? entry.type)?.toLowerCase() || 'class';
    const subjectName = asTrimmedString(
        entry.subject_name
        ?? entry.course
        ?? (entry.subject && typeof entry.subject === 'object' ? entry.subject.name : entry.subject)
    );
    const room = collectRoom(entry);
    const teachers = collectTeacherNames(entry);
    const groups = collectGroupCodes(entry);

    return {
        id: entry.id ?? entry.client_id ?? null,
        entry_type: entryType,
        year: asOptionalInteger(entry.year),
        week_series: asTrimmedString(entry.week_series ?? entry.weekSeries),
        day: asTrimmedString(entry.day)?.toLowerCase() || null,
        start_time: normalizeTime(entry.start_time ?? entry.timeStart ?? entry.startTime),
        end_time: normalizeTime(entry.end_time ?? entry.timeEnd ?? entry.endTime),
        subject_name: subjectName,
        teachers,
        teacher_name: teachers[0] || null,
        room_code: room.room_code,
        room_name: room.room_name,
        groups,
        title: asTrimmedString(entry.title) || asTrimmedString(entry.eventName),
        description: asTrimmedString(entry.description) || asTrimmedString(entry.notes),
        location: asTrimmedString(entry.location),
        exceptions: entry.exceptions ?? null,
        discard: Boolean(entry.discard),
        publish_identity: asTrimmedString(entry.publish_identity ?? entry.publishIdentity)
    };
}

export function serializeScheduleExport(entries, meta = {}) {
    const portableEntries = (Array.isArray(entries) ? entries : [])
        .map(serializeScheduleTransferEntry)
        .filter((entry) => entry && !isPlaceholderEntry(entry));

    return {
        format: SCHEDULE_TRANSFER_FORMAT,
        version: SCHEDULE_TRANSFER_VERSION,
        exported_at: meta.exportedAt || new Date().toISOString(),
        source: {
            draft_name: meta.draftName || null,
            display_name: meta.displayName || null,
            period_id: meta.periodId ?? null,
            period_name: meta.periodName || null
        },
        entries: portableEntries
    };
}

export function parseScheduleTransferPayload(data) {
    if (Array.isArray(data)) {
        return {
            entries: data.map(serializeScheduleTransferEntry).filter(Boolean),
            source: null,
            wrapped: false
        };
    }

    if (data && typeof data === 'object' && Array.isArray(data.entries)) {
        return {
            entries: data.entries.map(serializeScheduleTransferEntry).filter(Boolean),
            source: data.source || null,
            wrapped: true
        };
    }

    throw new Error('Schedule file must be a JSON array of entries, or an object with an "entries" array.');
}
